import * as ngCore from '@angular/core'
import { Cache } from '@attain-apps/data-utils'
import type * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../state'

/**
 * Displays the option lists for every entity in one dimension, from root to
 * leaf.
 */
@ngCore.Component({
  imports: [ds.input.select.multiple.Component],
  selector: 'atn-space-dimension-filter',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component<
  TTypeParams extends
    state.space.dimensionFilter.plugin.Plugin.TypeParams = state.space.dimensionFilter.plugin.Plugin.TypeParams,
> {
  /** Dimension-filter plugin that supplies the dimension and option state. */
  public readonly pluginSg = ngCore.input.required<
    state.space.dimensionFilter.plugin.Plugin<TTypeParams>
  >({
    alias: 'plugin',
  })

  /**
   * When true, omits the dimension's leaf entity from the rendered filters —
   * for callers that already let the user pick leaf-level records some other
   * way (e.g. a checkbox list) and would otherwise show that entity twice.
   *
   * @default false
   */
  public readonly excludeLeafSg = ngCore.input<boolean>(false, {
    alias: 'excludeLeaf',
  })

  /**
   * Overrides the displayed label, in place of the entity's own name (e.g.
   * "Responsible Person" instead of "Employee").
   *
   * @default entity.name
   */
  public readonly labelSg = ngCore.input<string | undefined>(undefined, {
    alias: 'label',
  })

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** @see {@link state.dms.schema.Feature.selectors.schema} */
  private readonly _schemaSg = this._schemaFeature.selectors.schema.asSignal

  /** DMS entities in hierarchy order for the supplied plugin's dimension. */
  public readonly entitiesSg = ngCore.computed(() => {
    const schema = this._schemaSg()
    const all = schema.getEntitiesInDimension(this.pluginSg().dimensionId)

    if (!this.excludeLeafSg()) {
      return all
    }

    const leaf = schema.tryGetLeafEntityOf(this.pluginSg().dimensionId)
    return leaf ? all.filter(entity => entity.id !== leaf.id) : all
  })

  /** Reactive option state and select items for one plugin entity. */
  public readonly optionsFor = Cache.fn(
    (entityId: ents.core.dms.models.Entity.Id) =>
      this.pluginSg()
        .selectors.optionsFor(entityId)
        .project(({ status, values, errorMessage }) => ({
          error: errorMessage,
          loading: status === 'loading',
          items: values.map(({ id, prefix, name }) => ({
            value: id,
            label: prefix ? `${prefix} - ${name}` : name,
          })),
        })).asSignal,
  )

  /** Reloads the options for one entity after an error. */
  public refetch(entityId: ents.core.dms.models.Entity.Id): void {
    this.pluginSg()
      .actions.loadOptions.from('DimensionFilter.Component.Refetch')
      .dispatch({ criteria: { entityId } })
  }

  /**
   * Updates the selection for one entity in the dimension-filter feature.
   */
  public selectionChanged(
    entityId: ents.core.dms.models.Entity.Id,
    recordIds: readonly (ents.core.dms.data.Record.Id | undefined)[],
  ): void {
    this.pluginSg()
      .actions.setSelection.from('DimensionFilter.Component.SelectionChanged')
      .dispatch({
        entityId,
        recordIds: recordIds.filter(TypeChecker.isNotNullish.raw),
      })
  }
}
